import { CrosshairIcon, LocateFixedIcon, WebhookIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import AvatarGroup from '@/components/atoms/Avatar/AvatarGroup/AvatarGroup';
import { FlowChartIcon } from '@/components/atoms/Icons/FlowChartIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';
import { selectAgentNamesMap } from '@/redux/presence/selectors';

export type DiffType = 'added' | 'modified' | 'removed' | 'all';
type Props = { component: string; type: DiffType; changes: string[] };

const icons = {
  intents: <LocateFixedIcon size={16} className="text-muted-foreground" />,
  dialogs: <FlowChartIcon />,
  entities: <CrosshairIcon size={16} className="text-muted-foreground" />,
  webhooks: <WebhookIcon size={16} className="text-muted-foreground" />,
};

const colorClass = {
  added: 'text-success',
  modified: 'text-foreground',
  removed: 'text-destructive',
};

export const Changes = ({ component, type, changes }: Props) => {
  const { t } = useTranslation();
  const agentNamesMap = useSelector(selectAgentNamesMap);
  return component === 'users' ? (
    changes.length > 0 && (
      <li className="flex items-center gap-1">
        <span className="flex items-center gap-4">
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('common.by')}{' '}
          </span>
          <AvatarGroup>
            {changes.map((change) => (
              <InfoTooltip key={change} content={agentNamesMap[change]} showArrow>
                <Avatar userId={change} size="small" hideStatus />
              </InfoTooltip>
            ))}
          </AvatarGroup>
        </span>
      </li>
    )
  ) : (
    <li className="flex items-center gap-1">
      <span className="text-sm leading-5 font-normal text-muted-foreground">{changes.length} </span>
      {icons[component]}
      <span className="text-sm leading-5 font-normal text-muted-foreground">
        {t(`versions.changes.${component}`, { count: changes.length })}
      </span>
      <span className={cn('text-sm leading-5 font-normal', colorClass[type])}>
        {t(`versions.${type}`)}
      </span>
    </li>
  );
};
